import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { parseFractionLikeInput as parseFraction } from "../../../../components/inputs/FractionInput";
import CirclePolygonAnglesDiagram from "../../../../components/diagrams/CirclePolygonAnglesDiagram";

const DEC_TOL = 0.1;
const GIVEN_ANGLES = [28, 32, 35, 38, 40, 42, 45, 48, 52, 55, 58, 60];

function rawText(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return String(value.raw ?? value.value ?? value.input ?? "");
}

function normalize(raw) {
  return String(raw ?? "").trim().replace(/\u2212/g, "-").replace(/\s+/g, "").replace(",", ".");
}

function pretty(raw) {
  return String(raw ?? "").trim().replace(".", "{,}");
}

export function generateTask() {
  const given = GIVEN_ANGLES[Math.floor(Math.random() * GIVEN_ANGLES.length)];
  return {
    type: "FindHiddenAngleInCircleTriangleTask",
    data: {
      given,
      answer: 90 - given,
    },
  };
}

export function renderTask({ data }) {
  const { given } = data;
  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      {"Punkty "}<InlineMath math="A" />{", "}<InlineMath math="B" />{", "}<InlineMath math="C" />{" i "}<InlineMath math="P" />{" le\u017c\u0105 na okr\u0119gu o \u015brodku "}<InlineMath math="O" />{". "}
      {"Punkty "}<InlineMath math="A" />{", "}<InlineMath math="O" />{" i "}<InlineMath math="B" />{" le\u017c\u0105 na jednej prostej. "}
      {"K\u0105t "}<InlineMath math={"\\angle BPC"} />{" ma miar\u0119 "}<InlineMath math={`${given}^{\\circ}`} />{". "}
      {"Oblicz miar\u0119 k\u0105ta "}<span style={{ whiteSpace: "nowrap" }}><InlineMath math={"\\alpha"} />{" przy wierzcho\u0142ku "}<InlineMath math="B" />{" tr\u00f3jk\u0105ta "}<InlineMath math="ABC" />{"."}</span>
      <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
        <CirclePolygonAnglesDiagram scenario="hiddenInscribedTriangle" givenDeg={{ value: given }} size={270} />
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={rawText(userAnswer)}
      onChange={(value) => onChange({ raw: rawText(value) })}
      disabled={disabled}
      labelLatex={"\\alpha"}
      suffixLatex={"^\\circ"}
      placeholder="np. 45"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = rawText(userAnswer);
  if (!raw.trim()) return false;
  const value = parseFraction(normalize(raw));
  return Number.isFinite(value) && Math.abs(value - data.answer) <= DEC_TOL;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`\\alpha=${data.answer}^{\\circ}`} />;
}

export function renderExplanation({ data }) {
  const { given, answer } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <strong>Krok 1.</strong> {"K\u0105ty wpisane oparte na tym samym \u0142uku maj\u0105 r\u00f3wne miary. K\u0105ty "}
      <InlineMath math={"\\angle BPC"} />{" i "}<InlineMath math={"\\angle BAC"} />{" opieraj\u0105 si\u0119 na \u0142uku "}<InlineMath math="BC" />{"."}
      <BlockMath math={`\\angle BAC=${given}^{\\circ}`} />
      <strong>Krok 2.</strong> {"Poniewa\u017c punkty "}<InlineMath math="A" />{", "}<InlineMath math="O" />{" i "}<InlineMath math="B" />{" le\u017c\u0105 na jednej prostej, odcinek "}<InlineMath math="AB" />{" jest \u015brednic\u0105 okr\u0119gu. K\u0105t wpisany oparty na \u015brednicy jest prosty."}
      <BlockMath math={"\\angle ACB=90^{\\circ}"} />
      <div style={{ margin: "10px 0", display: "flex", justifyContent: "center" }}>
        <CirclePolygonAnglesDiagram
          scenario="hiddenInscribedTriangle"
          givenDeg={{ value: given }}
          size={270}
          showRightAngleAtC
        />
      </div>
      <strong>Krok 3.</strong> {"Korzystamy z sumy k\u0105t\u00f3w w tr\u00f3jk\u0105cie "}<InlineMath math="ABC" />{"."}
      <BlockMath math={`\\alpha=180^{\\circ}-90^{\\circ}-${given}^{\\circ}=${answer}^{\\circ}`} />
      <div style={{ marginTop: 10 }}>
        {"Odpowied\u017a: "}<InlineMath math={`\\alpha=${answer}^{\\circ}`} />{"."}
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = rawText(userAnswer);
  if (!raw.trim()) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`\\alpha=${pretty(raw)}^{\\circ}`} />;
}

const taskModule = {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};

export default taskModule;
